import {
  PDF_FONT_SIZE,
  type ReportTableHeaderCell,
  type ReportTableProps,
} from '@/components';
import type {
  ReportOrganizingTableFormat4Model,
  ReportOrganizingTableFormat4SummaryByAccountModel,
} from '@/features/mold/models';
import {
  format4HeaderModifier,
  format4ValueConverter,
} from '@/features/mold/utils/organizing-table/format4/get-csv';
import { t } from '@/i18n';
import { formatNumberForPdf } from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';
import { centerTextStyle } from '../../pdf/pdf-utils';

type TableBodyElement = ReportTableProps['data'][number];

const headerStyle: CSSProperties = {
  fontSize: PDF_FONT_SIZE.XS,
  textAlign: 'center',
};

const MONEY_STYLE: CSSProperties = { textAlign: 'right' };

const sectionCellRatio = (isGeneralSpecialAccount?: boolean) =>
  isGeneralSpecialAccount ? [1, 2, 2, 1] : [0.9, 2, 2, 2, 0.9, 1.2];
const projectCountAndReflectedAmountCellRatios = [1, 1.5];

const createReflectionStatusCell = ({
  caption,
  currentYear,
  isGeneralSpecialAccount,
}: {
  caption: string;
  currentYear: number;
  isGeneralSpecialAccount?: boolean;
}): ReportTableHeaderCell => {
  const header = format4HeaderModifier({ currentYear });

  const projectCountAndReflectedAmountCell: ReportTableHeaderCell = {
    type: 'multiple',
    ratio: 1,
    cell: {
      cellRatios: projectCountAndReflectedAmountCellRatios,
      flexCells: [
        {
          ...centerTextStyle,
          value: header.projectCount,
          textStyle: headerStyle,
        },
        {
          ...centerTextStyle,
          value: header.reflectedAmount,
          textStyle: headerStyle,
        },
      ],
    },
  };

  return {
    type: 'multiple',
    ratio: isGeneralSpecialAccount ? 2 : 3,
    cell: {
      vertical: true,
      cellRatios: [1, 2],
      flexCells: [
        {
          ...centerTextStyle,
          value: caption,
          textStyle: headerStyle,
        },
        {
          type: 'multiple',
          cell: {
            cellRatios: sectionCellRatio(isGeneralSpecialAccount),
            flexCells: [
              {
                ...centerTextStyle,
                value: header.previousYearProjectCountHeaderName,
                textStyle: headerStyle,
              },
              {
                type: 'multiple',
                cell: {
                  vertical: true,
                  cellRatios: [1, 2],
                  flexCells: [
                    {
                      ...centerTextStyle,
                      value: header.abolition,
                      textStyle: headerStyle,
                    },
                    projectCountAndReflectedAmountCell,
                  ],
                },
              },
              {
                type: 'multiple',
                cell: {
                  cellRatios: [1, 2],
                  vertical: true,
                  flexCells: [
                    {
                      ...centerTextStyle,
                      value: header.reduction,
                      textStyle: headerStyle,
                    },
                    projectCountAndReflectedAmountCell,
                  ],
                },
              },
              ...((!isGeneralSpecialAccount
                ? [
                    {
                      type: 'multiple',
                      cell: {
                        cellRatios: [1, 2],
                        vertical: true,
                        flexCells: [
                          {
                            ...centerTextStyle,
                            value: header.abolitionReductionTotal,
                            textStyle: headerStyle,
                          },
                          projectCountAndReflectedAmountCell,
                        ],
                      },
                    },
                  ]
                : []) as ReportTableProps['header']),
              {
                ...centerTextStyle,
                value: header.improvementProjectCountHeaderName,
                textStyle: headerStyle,
              },
              ...((!isGeneralSpecialAccount
                ? [
                    {
                      ...centerTextStyle,
                      value: header.projectRequestedAmountHeaderName,
                      textStyle: headerStyle,
                    },
                  ]
                : []) as ReportTableProps['header']),
            ],
          },
        },
      ],
    },
  };
};

export const getFormat4PdfHeader = ({
  currentYear,
}: {
  currentYear: number;
}): ReportTableProps['header'] => {
  const header = format4HeaderModifier({ currentYear });

  return [
    {
      value: header.jurisdiction,
      ratio: 1,
      textStyle: headerStyle,
    },
    createReflectionStatusCell({
      currentYear,
      caption: header.generalSpecialAccount,
      isGeneralSpecialAccount: true,
    }),
    createReflectionStatusCell({
      currentYear,
      caption: header.generalAccount,
    }),
    createReflectionStatusCell({
      currentYear,
      caption: header.specialAccount,
    }),
  ];
};

const getCells = ({
  general,
  generalSpecial,
  special,
}: z.infer<
  typeof ReportOrganizingTableFormat4SummaryByAccountModel
>): TableBodyElement['row'] => {
  return [
    {
      type: 'multiple',
      cell: {
        cellRatios: sectionCellRatio(true),
        flexCells: [
          {
            ...centerTextStyle,
            value: formatNumberForPdf(generalSpecial.previousYearProjectCount),
            textStyle: MONEY_STYLE,
          },
          {
            type: 'multiple',
            cell: {
              cellRatios: projectCountAndReflectedAmountCellRatios,
              flexCells: [
                {
                  ...centerTextStyle,
                  value: formatNumberForPdf(
                    generalSpecial.abolitionProjectCount,
                  ),
                  textStyle: MONEY_STYLE,
                },
                {
                  ...centerTextStyle,
                  value: format4ValueConverter.abolitionProjectReflectedAmount({
                    data: generalSpecial,
                    type: 'pdf',
                  }),
                  textStyle: MONEY_STYLE,
                },
              ],
            },
          },
          {
            type: 'multiple',
            cell: {
              cellRatios: projectCountAndReflectedAmountCellRatios,
              flexCells: [
                {
                  ...centerTextStyle,
                  value: formatNumberForPdf(
                    generalSpecial.reductionProjectCount,
                  ),
                  textStyle: MONEY_STYLE,
                },
                {
                  ...centerTextStyle,
                  value: format4ValueConverter.reductionProjectReflectedAmount({
                    data: generalSpecial,
                    type: 'pdf',
                  }),
                  textStyle: MONEY_STYLE,
                },
              ],
            },
          },
          {
            ...centerTextStyle,
            value: formatNumberForPdf(generalSpecial.improvementProjectCount),
            textStyle: MONEY_STYLE,
          },
        ],
      },
    },
    {
      type: 'multiple',
      cell: {
        cellRatios: sectionCellRatio(),
        flexCells: [
          {
            ...centerTextStyle,
            value: formatNumberForPdf(general.previousYearProjectCount),
            textStyle: MONEY_STYLE,
          },
          {
            type: 'multiple',
            cell: {
              cellRatios: projectCountAndReflectedAmountCellRatios,
              flexCells: [
                {
                  ...centerTextStyle,
                  value: formatNumberForPdf(general.abolitionProjectCount),
                  textStyle: MONEY_STYLE,
                },
                {
                  ...centerTextStyle,
                  value: format4ValueConverter.abolitionProjectReflectedAmount({
                    data: general,
                    type: 'pdf',
                  }),
                  textStyle: MONEY_STYLE,
                },
              ],
            },
          },
          {
            type: 'multiple',
            cell: {
              cellRatios: projectCountAndReflectedAmountCellRatios,
              flexCells: [
                {
                  ...centerTextStyle,
                  value: formatNumberForPdf(general.reductionProjectCount),
                  textStyle: MONEY_STYLE,
                },
                {
                  ...centerTextStyle,
                  value: format4ValueConverter.reductionProjectReflectedAmount({
                    data: general,
                    type: 'pdf',
                  }),
                  textStyle: MONEY_STYLE,
                },
              ],
            },
          },
          {
            type: 'multiple',
            cell: {
              cellRatios: projectCountAndReflectedAmountCellRatios,
              flexCells: [
                {
                  ...centerTextStyle,
                  value: formatNumberForPdf(
                    general.abolitionReductionProjectCount,
                  ),
                  textStyle: MONEY_STYLE,
                },
                {
                  ...centerTextStyle,
                  value:
                    format4ValueConverter.abolitionReductionProjectReflectedAmount(
                      { data: general, type: 'pdf' },
                    ),
                  textStyle: MONEY_STYLE,
                },
              ],
            },
          },
          {
            ...centerTextStyle,
            value: formatNumberForPdf(general.improvementProjectCount),
            textStyle: MONEY_STYLE,
          },
          {
            ...centerTextStyle,
            value: format4ValueConverter.projectRequestedAmount({
              data: general,
              type: 'pdf',
            }),
            textStyle: MONEY_STYLE,
          },
        ],
      },
    },
    {
      type: 'multiple',
      cell: {
        cellRatios: sectionCellRatio(),
        flexCells: [
          {
            ...centerTextStyle,
            value: formatNumberForPdf(special.previousYearProjectCount),
            textStyle: MONEY_STYLE,
          },
          {
            type: 'multiple',
            cell: {
              cellRatios: projectCountAndReflectedAmountCellRatios,
              flexCells: [
                {
                  ...centerTextStyle,
                  value: formatNumberForPdf(special.abolitionProjectCount),
                  textStyle: MONEY_STYLE,
                },
                {
                  ...centerTextStyle,
                  value: format4ValueConverter.abolitionProjectReflectedAmount({
                    data: special,
                    type: 'pdf',
                  }),
                  textStyle: MONEY_STYLE,
                },
              ],
            },
          },
          {
            type: 'multiple',
            cell: {
              cellRatios: projectCountAndReflectedAmountCellRatios,
              flexCells: [
                {
                  ...centerTextStyle,
                  value: formatNumberForPdf(special.reductionProjectCount),
                  textStyle: MONEY_STYLE,
                },
                {
                  ...centerTextStyle,
                  value: format4ValueConverter.reductionProjectReflectedAmount({
                    data: special,
                    type: 'pdf',
                  }),
                  textStyle: MONEY_STYLE,
                },
              ],
            },
          },
          {
            type: 'multiple',
            cell: {
              cellRatios: projectCountAndReflectedAmountCellRatios,
              flexCells: [
                {
                  ...centerTextStyle,
                  value: formatNumberForPdf(
                    special.abolitionReductionProjectCount,
                  ),
                  textStyle: MONEY_STYLE,
                },
                {
                  ...centerTextStyle,
                  value:
                    format4ValueConverter.abolitionReductionProjectReflectedAmount(
                      { data: special, type: 'pdf' },
                    ),
                  textStyle: MONEY_STYLE,
                },
              ],
            },
          },
          {
            ...centerTextStyle,
            value: formatNumberForPdf(special.improvementProjectCount),
            textStyle: MONEY_STYLE,
          },
          {
            ...centerTextStyle,
            value: format4ValueConverter.projectRequestedAmount({
              data: special,
              type: 'pdf',
            }),
            textStyle: MONEY_STYLE,
          },
        ],
      },
    },
  ];
};

export const getForma4PdfBody = ({
  organizations,
}: {
  organizations: z.infer<
    typeof ReportOrganizingTableFormat4Model
  >['organizations'];
}): ReportTableProps['data'] => {
  return organizations.flatMap<TableBodyElement>(
    ({ name, summaryByAccount }) => {
      return [
        {
          row: [
            { value: name },
            ...getCells({
              ...summaryByAccount,
            }),
          ],
        },
      ];
    },
  );
};

export const getFormat4PdfFooter = (
  summaryByAccount: z.infer<
    typeof ReportOrganizingTableFormat4SummaryByAccountModel
  >,
): ReportTableProps['footer'] => {
  return [
    {
      row: [
        { value: t('total'), textStyle: { textAlign: 'center' } },
        ...getCells({ ...summaryByAccount }),
      ],
    },
  ];
};
